import type { WidgetConfig, UrgencyKey } from './config';
import { URGENCY_ORDER } from './config';
import { STYLES } from './styles';
import { captureContext, installErrorCapture } from './context';
import { submitRequest, uploadAttachment, uploadVoiceNote } from './api';
import { installRouteHooks, onRouteChange, shouldShowAt } from './visibility';
import {
  CLOSE_SVG, ARROW_SVG, MIC_SVG, TOOL_CAMERA_SVG, TOOL_VIDEO_SVG, TOOL_CLIP_SVG,
  VIDEO_SVG, FILE_SVG, STOP_SVG, SPINNER_SVG, PLAY_SVG, PAUSE_SVG, REDO_SVG, X_SMALL_SVG, TICK_SVG,
  renderLauncherIcon,
} from './icons';
import { annotate } from './annotator';
import { captureScreenshot, isScreenshotSupported, isRecordingSupported, startScreenRecording, RECORDING_LIMITS, type RecordingHandle } from './capture';
import { installRecordingOverlay, type OverlayHandle } from './recordingOverlay';
import { startVoice, waveform, type VoiceSession } from './voice';

/**
 * The widget panel — FR-2026-0105, Direction A "Composer".
 *
 *   header      firm logo, "Tell <firm>", a one-line expectation
 *   composer    subject + details + evidence (thumbnails, voice notes) and a
 *               toolbar: Voice (accent), Screenshot, Record screen (desktop),
 *               File; character count on the right
 *   urgency     four chips (radio group) mapped onto the template severities
 *   email+Send  side by side on desktop, stacked on phones
 *   footer      what is attached automatically · Powered by Klientele
 *   done        "<firm> has it", what arrived, the reference with Copy,
 *               where replies go, "Send something else"
 */

interface WidgetState {
  open: boolean;
  submitting: boolean;
  manualHidden: boolean; // set by runtime API .hide()
}

type AttachmentKind = 'screenshot' | 'recording' | 'file';
type UploadStatus = 'queued' | 'uploading' | 'uploaded' | 'failed';

interface QueuedAttachment {
  id: string;
  blob: Blob;
  name: string;
  kind: AttachmentKind;
  /** data: URL for image previews (blob: URLs fail strict host img-src). */
  previewUrl: string | null;
  status: UploadStatus;
  errorMessage?: string;
}

interface VoiceNote {
  id: string;
  blob: Blob;
  durationMs: number;
  bars: number[];
  status: UploadStatus;
  errorMessage?: string;
}

/** Composer draft — survives re-renders of the form, cleared on close / send. */
interface Draft {
  subject: string;
  details: string;
  email: string;
  urgency: UrgencyKey;
}

const MAX_FILES = 5; // backend cap per request
const MAX_VOICE_NOTES = 5;
const VOICE_CAP_MS = 2 * 60 * 1000;
const DETAILS_MAX = 10_000;
const BAR_COUNT = 34;

let idCounter = 0;
const nextId = (p: string) => `${p}-${Date.now()}-${++idCounter}`;

export class KlienteleWidget {
  private config: WidgetConfig;
  private host: HTMLDivElement;
  private root: ShadowRoot;
  private launcher!: HTMLButtonElement;
  private state: WidgetState = { open: false, submitting: false, manualHidden: false };
  private modalEl: HTMLDivElement | null = null;
  /** Detaches the on-screen-keyboard tracking added while the panel is open (TK-2026-0555). */
  private untrackViewport: (() => void) | null = null;
  private panel: HTMLDivElement | null = null;
  private attachments: QueuedAttachment[] = [];
  private voiceNotes: VoiceNote[] = [];
  private draft: Draft;
  private recording: RecordingHandle | null = null;
  private recordingInterval: ReturnType<typeof setInterval> | null = null;
  private recordingOverlay: OverlayHandle | null = null;
  /** Live microphone session, and the note it replaces (re-record) if any. */
  private voice: { session: VoiceSession; replaces: string | null; tick: ReturnType<typeof setInterval> } | null = null;
  private player: { audio: HTMLAudioElement; noteId: string; url: string; raf: number } | null = null;
  private returnFocus: HTMLElement | null = null;

  constructor(config: WidgetConfig) {
    this.config = config;
    this.draft = this.emptyDraft();
    installErrorCapture();
    installRouteHooks();

    this.host = document.createElement('div');
    this.host.id = 'klientele-widget-host';
    this.host.style.cssText = 'all: initial;';
    this.root = this.host.attachShadow({ mode: 'closed' });

    const accent = config.color ?? config.server.firm.accentColor ?? '#2563EB';
    const style = document.createElement('style');
    style.textContent = STYLES
      .replace(/\{\{COLOR\}\}/g, config.color ?? config.server.firm.accentColor ?? '#0A0A0A')
      .replace(/\{\{ACCENT\}\}/g, accent)
      .replace(/\{\{ACCENT_SOFT\}\}/g, `${accent}14`);
    this.root.appendChild(style);

    this.renderLauncher();
    this.evaluateVisibility();
    onRouteChange(() => this.evaluateVisibility());
  }

  mount(): void {
    if (document.body) {
      document.body.appendChild(this.host);
    } else {
      window.addEventListener('DOMContentLoaded', () => document.body.appendChild(this.host), { once: true });
    }
  }

  // ------- Public runtime API -------
  hide(): void { this.state.manualHidden = true; this.evaluateVisibility(); }
  show(): void { this.state.manualHidden = false; this.evaluateVisibility(); }
  setVisible(visible: boolean): void { this.state.manualHidden = !visible; this.evaluateVisibility(); }
  open(): void {
    if (this.state.open) return;
    this.state.open = true;
    this.returnFocus = this.launcher;
    this.renderShell();
    this.renderForm();
  }
  close(): void {
    if (!this.state.open) return;
    this.state.open = false;
    // Always reset the submitting flag — a hung submit must not bounce every
    // Send click after reopen.
    this.state.submitting = false;
    if (this.recording) { try { this.recording.stop(); } catch { /* already stopped */ } this.recording = null; }
    if (this.recordingInterval) { clearInterval(this.recordingInterval); this.recordingInterval = null; }
    this.root.querySelector<HTMLElement>('.df-rec-pill')?.remove();
    this.recordingOverlay?.cleanup();
    this.recordingOverlay = null;
    this.cancelVoice();
    this.stopPlayback();
    this.attachments = [];
    this.voiceNotes = [];
    this.draft = this.emptyDraft();
    this.untrackViewport?.();
    this.untrackViewport = null;
    if (this.modalEl) { this.modalEl.remove(); this.modalEl = null; this.panel = null; }
    if (this.returnFocus && !this.launcher.classList.contains('df-hidden')) this.returnFocus.focus();
  }

  // ------- Words -------

  private get firmName(): string {
    return this.config.server.firm.name || 'us';
  }

  private emptyDraft(): Draft {
    const s = this.config.server;
    // data-default-urgency picks the chip filing at that urgency; otherwise
    // the template's default ("Slowing me down").
    const byUrgency = this.config.defaultUrgency
      ? s.urgency.find((u) => u.urgency === this.config.defaultUrgency)?.key
      : undefined;
    return { subject: '', details: '', email: '', urgency: byUrgency ?? s.defaultUrgency };
  }

  // ------- Internals -------

  private evaluateVisibility(): void {
    const allowedByPath = shouldShowAt(window.location.pathname, this.config.hidePatterns, this.config.showPatterns);
    const visible = allowedByPath && !this.state.manualHidden;
    this.launcher.classList.toggle('df-hidden', !visible);
    if (!visible && this.state.open) this.close();
  }

  private renderLauncher(): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `df-launcher df-launcher-${this.config.launcher} df-pos-${this.config.position}`;
    btn.setAttribute('aria-label', this.config.label);
    btn.setAttribute('aria-haspopup', 'dialog');
    if (this.config.launcher === 'icon') btn.title = this.config.label;
    btn.innerHTML = `<span class="df-icon" aria-hidden="true">${renderLauncherIcon(this.config.iconUrl)}</span><span class="df-label-text">${escapeHtml(this.config.label)}</span>`;
    btn.addEventListener('click', () => this.open());
    this.launcher = btn;
    this.root.appendChild(btn);
  }

  /** Backdrop + panel frame; the form and the done screen render into it. */
  private renderShell(): void {
    const backdrop = document.createElement('div');
    backdrop.className = `df-backdrop df-at-${this.config.position}`;
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop && !this.voice) this.close(); });
    backdrop.addEventListener('keydown', (e) => this.onKeydown(e));

    const panel = document.createElement('div');
    panel.className = 'df-modal';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'df-title');
    panel.setAttribute('aria-describedby', 'df-subtitle');
    panel.tabIndex = -1;

    backdrop.appendChild(panel);
    this.root.appendChild(backdrop);
    this.modalEl = backdrop;
    this.panel = panel;
    this.trackViewport(backdrop, panel);
  }

  /**
   * TK-2026-0555: keep the panel above a phone's on-screen keyboard.
   *
   * A `position: fixed; inset: 0` backdrop is sized to the layout viewport,
   * which the keyboard does not shrink (iOS Safari, and Android with an
   * overlaying keyboard), so a bottom sheet ends up under the keys. The
   * visual viewport is what the user can actually see: pin the backdrop to
   * it, let the panel cap its height to it, and bring the focused field back
   * into view once the keyboard has settled.
   */
  private trackViewport(backdrop: HTMLDivElement, panel: HTMLDivElement): void {
    const vv = window.visualViewport;
    if (!vv) return;
    const fit = () => {
      backdrop.style.top = `${vv.offsetTop}px`;
      backdrop.style.height = `${vv.height}px`;
      backdrop.style.bottom = 'auto';
      backdrop.style.setProperty('--df-vvh', `${vv.height}px`);
    };
    const keepFocusInView = () => {
      const active = this.root instanceof ShadowRoot ? this.root.activeElement : document.activeElement;
      if (active instanceof HTMLElement && panel.contains(active)) {
        active.scrollIntoView({ block: 'nearest' });
      }
    };
    const onResize = () => { fit(); window.requestAnimationFrame(keepFocusInView); };
    const onFocusIn = () => window.setTimeout(keepFocusInView, 300);
    fit();
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', fit);
    panel.addEventListener('focusin', onFocusIn);
    this.untrackViewport = () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', fit);
      panel.removeEventListener('focusin', onFocusIn);
    };
  }

  /** Escape closes; Tab stays inside the panel (focus trap). */
  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (this.voice) { this.voice.session.stop(); return; }
      this.close();
      return;
    }
    if (e.key !== 'Tab' || !this.panel) return;
    const focusables = Array.from(
      this.panel.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]'),
    ).filter((el) => el.offsetParent !== null || el === this.root.activeElement);
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = this.root.activeElement as HTMLElement | null;
    if (e.shiftKey && (active === first || !active || !this.panel.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !active || !this.panel.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  private headerHtml(subtitle: string): string {
    const firm = this.config.server.firm;
    const title = this.config.title ?? `Tell ${firm.name || 'us'}`;
    const logo = firm.logoUrl
      ? `<span class="df-logo df-logo-img" aria-hidden="true"><img src="${escapeAttr(firm.logoUrl)}" alt="" data-role="logo-img" /></span>`
      : `<span class="df-logo" aria-hidden="true">${escapeHtml(initials(firm.name))}</span>`;
    return `
      <div class="df-grab" aria-hidden="true"></div>
      <div class="df-header">
        ${logo}
        <div class="df-head-text">
          <h2 id="df-title" class="df-title">${escapeHtml(title)}</h2>
          <span id="df-subtitle" class="df-subtitle">${escapeHtml(subtitle)}</span>
        </div>
        <button type="button" class="df-close" data-action="close" aria-label="Close">${CLOSE_SVG}</button>
      </div>`;
  }

  /** A logo that fails to load (host CSP, removed file) falls back to initials. */
  private wireLogoFallback(): void {
    const img = this.panel?.querySelector<HTMLImageElement>('[data-role="logo-img"]');
    if (!img) return;
    img.addEventListener('error', () => {
      const tile = img.parentElement;
      if (!tile) return;
      tile.classList.remove('df-logo-img');
      tile.textContent = initials(this.config.server.firm.name);
    }, { once: true });
  }

  private footerHtml(): string {
    return `
      <div class="df-footer">
        <span><span class="df-foot-long">Page and browser details attached automatically</span><span class="df-foot-short">Page details attached automatically</span></span>
        <a href="https://klientele.io" target="_blank" rel="noopener">Powered by Klientele</a>
      </div>`;
  }

  // ------- The form -------

  private renderForm(): void {
    const panel = this.panel;
    if (!panel) return;
    const s = this.config.server;
    const noun = s.nouns.request;
    panel.setAttribute('aria-label', `New ${noun.toLowerCase()} for ${this.firmName}`);

    const tools = [
      `<button type="button" class="df-tool df-mic" data-action="voice" aria-label="Record a voice note" title="Record a voice note, up to ${formatDuration(this.voiceCapMs() / 1000)}">${MIC_SVG}<span>Voice</span></button>`,
      isScreenshotSupported() ? `<button type="button" class="df-tool" data-action="screenshot" aria-label="Add a screenshot of this page" title="Screenshot this page">${TOOL_CAMERA_SVG}</button>` : '',
      isRecordingSupported() ? `<button type="button" class="df-tool" data-action="record" aria-label="Record your screen" title="Record your screen (up to ${formatDuration(Math.floor(RECORDING_LIMITS.maxMs / 1000))})">${TOOL_VIDEO_SVG}</button>` : '',
      `<button type="button" class="df-tool" data-action="file" aria-label="Attach a file" title="Attach a file">${TOOL_CLIP_SVG}</button>`,
    ].join('');

    const chips = URGENCY_ORDER.map((key) => {
      const on = this.draft.urgency === key;
      return `<button type="button" class="df-chip" role="radio" aria-checked="${on}" tabindex="${on ? 0 : -1}" data-urgency="${key}">${escapeHtml(this.config.urgencyLabels[key])}</button>`;
    }).join('');

    const emailNote = this.config.requireEmail ? 'required, for the reply' : 'for the reply';

    panel.innerHTML = `
      ${this.headerHtml("Goes to their team. You'll get a reference.")}
      <div class="df-modal-scroll">
        <form class="df-form" novalidate>
          <div class="df-msg df-hidden" role="alert" data-role="error"></div>
          <div class="df-composer">
            <label class="df-sr" for="df-subject">Subject</label>
            <input class="df-subj" id="df-subject" name="subject" type="text" maxlength="500" placeholder="Subject" autocomplete="off" />
            <label class="df-sr" for="df-details">Details</label>
            <textarea class="df-body" id="df-details" name="details" maxlength="${DETAILS_MAX}" rows="3" placeholder="What happened? Type, or record a voice note." aria-describedby="df-count"></textarea>
            <div class="df-evidence" data-role="evidence"></div>
            <div class="df-bar">
              <div class="df-tools" role="toolbar" aria-label="Add to your message">${tools}</div>
              <span class="df-count" id="df-count" data-role="count" aria-live="off"></span>
            </div>
          </div>
          <div>
            <div class="df-lbl" id="df-urgency-label">How much is this in your way?</div>
            <div class="df-chips" role="radiogroup" aria-labelledby="df-urgency-label">${chips}</div>
          </div>
          <div class="df-send-row">
            <div class="df-field">
              <label class="df-lbl" for="df-email">Your email <em>${emailNote}</em></label>
              <input class="df-input" id="df-email" name="email" type="email" maxlength="320" placeholder="you@company.com" autocomplete="email" ${this.config.requireEmail ? 'required aria-required="true"' : ''} />
            </div>
            <button type="submit" class="df-send" data-role="send"><span data-role="send-label">Send</span>${ARROW_SVG}</button>
          </div>
        </form>
      </div>
      ${this.footerHtml()}
    `;
    this.wireLogoFallback();

    const subject = panel.querySelector<HTMLInputElement>('#df-subject')!;
    const details = panel.querySelector<HTMLTextAreaElement>('#df-details')!;
    const email = panel.querySelector<HTMLInputElement>('#df-email')!;
    subject.value = this.draft.subject;
    details.value = this.draft.details;
    email.value = this.draft.email;
    subject.addEventListener('input', () => { this.draft.subject = subject.value; });
    details.addEventListener('input', () => { this.draft.details = details.value; this.updateCount(); this.autosize(details); });
    email.addEventListener('input', () => { this.draft.email = email.value; email.removeAttribute('aria-invalid'); });
    this.updateCount();

    // Hidden file input — used by the File tool.
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = 'image/*,video/*,audio/*,application/pdf,.csv,.txt,.log,.json,.xml,.doc,.docx,.xls,.xlsx';
    fileInput.style.display = 'none';
    fileInput.tabIndex = -1;
    fileInput.addEventListener('change', () => {
      if (fileInput.files) for (const f of Array.from(fileInput.files)) this.addFile(f);
      fileInput.value = '';
    });
    panel.appendChild(fileInput);

    panel.onclick = (e) => {
      const target = e.target as HTMLElement;
      const chip = target.closest<HTMLElement>('[data-urgency]');
      if (chip) { this.selectUrgency(chip.dataset.urgency as UrgencyKey, true); return; }
      const el = target.closest<HTMLElement>('[data-action]');
      if (!el) return;
      const action = el.dataset.action;
      const id = el.dataset.id ?? '';
      if (action === 'close') this.close();
      else if (action === 'voice') void this.onVoiceClick(null);
      else if (action === 'voice-stop') this.voice?.session.stop();
      else if (action === 'voice-play') this.togglePlayback(id);
      else if (action === 'voice-redo') void this.onVoiceClick(id);
      else if (action === 'voice-remove') this.removeVoiceNote(id);
      else if (action === 'screenshot') void this.onScreenshotClick();
      else if (action === 'record') void this.onRecordClick();
      else if (action === 'file') fileInput.click();
      else if (action === 'remove') this.removeAttachment(id);
      else if (action === 'open') this.openAttachment(id);
    };
    panel.querySelector<HTMLElement>('[role="radiogroup"]')!.addEventListener('keydown', (e) => this.onChipKey(e));

    const form = panel.querySelector<HTMLFormElement>('.df-form')!;
    form.addEventListener('submit', (e) => { e.preventDefault(); void this.onSubmit(form); });

    this.renderEvidence();
    queueMicrotask(() => subject.focus());
  }

  private autosize(ta: HTMLTextAreaElement): void {
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
  }

  private updateCount(): void {
    const el = this.panel?.querySelector<HTMLElement>('[data-role="count"]');
    if (!el) return;
    const left = DETAILS_MAX - this.draft.details.length;
    el.textContent = `${left.toLocaleString('en-US')} left`;
    el.classList.toggle('df-low', left < 200);
  }

  // ------- Urgency chips (radio group, roving tabindex) -------

  private selectUrgency(key: UrgencyKey, focus: boolean): void {
    this.draft.urgency = key;
    this.panel?.querySelectorAll<HTMLElement>('[data-urgency]').forEach((chip) => {
      const on = chip.dataset.urgency === key;
      chip.setAttribute('aria-checked', String(on));
      chip.tabIndex = on ? 0 : -1;
      if (on && focus) chip.focus();
    });
  }

  private onChipKey(e: KeyboardEvent): void {
    const i = URGENCY_ORDER.indexOf(this.draft.urgency);
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % URGENCY_ORDER.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + URGENCY_ORDER.length) % URGENCY_ORDER.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = URGENCY_ORDER.length - 1;
    if (next < 0) return;
    e.preventDefault();
    this.selectUrgency(URGENCY_ORDER[next]!, true);
  }

  // ------- Evidence: thumbnails + voice notes inside the composer -------

  private renderEvidence(): void {
    const box = this.panel?.querySelector<HTMLDivElement>('[data-role="evidence"]');
    if (!box) return;
    const thumbs = this.attachments.map((a) => this.thumbHtml(a)).join('');
    const notes = this.voiceNotes.map((n) => (this.voice?.replaces === n.id ? this.recHtml() : this.noteHtml(n))).join('');
    const live = this.voice && !this.voice.replaces ? this.recHtml() : '';
    box.innerHTML = `${thumbs ? `<div class="df-thumbs">${thumbs}</div>` : ''}${notes}${live}`;
    this.updateToolState();
  }

  private thumbHtml(a: QueuedAttachment): string {
    const opens = a.status === 'queued' && (a.kind === 'screenshot' || a.kind === 'recording');
    const label = a.kind === 'screenshot' ? `Screenshot. Open to mark it up`
      : a.kind === 'recording' ? `Screen recording ${a.name}. Open to watch`
      : `File ${a.name}`;
    const inner = a.previewUrl && a.blob.type.startsWith('image/')
      ? `<img src="${a.previewUrl}" alt="" />`
      : `${a.kind === 'recording' ? VIDEO_SVG : FILE_SVG}<span class="df-thumb-name">${escapeHtml(a.name)}</span>`;
    const cls = ['df-thumb', opens ? 'df-thumb-open' : '', a.status === 'failed' ? 'df-failed' : '', a.status === 'uploading' ? 'df-busy' : ''].join(' ');
    const title = a.errorMessage ? `${a.name}: ${a.errorMessage}` : label;
    const main = opens
      ? `<button type="button" class="${cls}" data-action="open" data-id="${a.id}" aria-label="${escapeAttr(label)}" title="${escapeAttr(title)}">${inner}</button>`
      : `<span class="${cls}" role="img" aria-label="${escapeAttr(label)}" title="${escapeAttr(title)}">${inner}</span>`;
    const remove = a.status === 'queued'
      ? `<button type="button" class="df-thumb-x" data-action="remove" data-id="${a.id}" aria-label="Remove ${escapeAttr(a.kind === 'file' ? a.name : a.kind)}">${X_SMALL_SVG}</button>`
      : '';
    return `<span style="position:relative;display:inline-block">${main}${remove}</span>`;
  }

  private noteHtml(n: VoiceNote): string {
    const playing = this.player?.noteId === n.id;
    const bars = n.bars.map((h) => `<b style="height:${Math.round(h * 100)}%"></b>`).join('');
    const editable = n.status === 'queued' && !this.voice;
    const title = n.errorMessage ? ` title="${escapeAttr(n.errorMessage)}"` : '';
    return `
      <div class="df-voice${playing ? ' df-playing' : ''}${n.status === 'failed' ? ' df-failed' : ''}" data-note="${n.id}"${title}>
        <button type="button" class="df-play" data-action="voice-play" data-id="${n.id}" aria-label="${playing ? 'Pause' : 'Play'} voice note, ${formatDuration(Math.round(n.durationMs / 1000))}">${playing ? PAUSE_SVG : PLAY_SVG}</button>
        <div class="df-bars" aria-hidden="true">${bars}</div>
        <span class="df-tm">${formatDuration(Math.round(n.durationMs / 1000))}</span>
        ${editable ? `<button type="button" class="df-icon-btn" data-action="voice-redo" data-id="${n.id}" aria-label="Record this voice note again" title="Record again">${REDO_SVG}</button>
        <button type="button" class="df-icon-btn" data-action="voice-remove" data-id="${n.id}" aria-label="Remove voice note" title="Remove">${X_SMALL_SVG}</button>` : ''}
      </div>`;
  }

  private recHtml(): string {
    const bars = Array.from({ length: BAR_COUNT }, () => '<b style="height:12%"></b>').join('');
    return `
      <div class="df-rec" role="status" aria-label="Recording a voice note">
        <span class="df-rec-dot" aria-hidden="true"></span>
        <div class="df-bars" data-role="live-bars" aria-hidden="true">${bars}</div>
        <span class="df-tm" data-role="live-time">0:00 / ${formatDuration(this.voiceCapMs() / 1000)}</span>
        <button type="button" class="df-stop" data-action="voice-stop">Stop</button>
      </div>`;
  }

  /** Disable capture tools while the microphone is live or a send is in flight. */
  private updateToolState(): void {
    const busy = !!this.voice || this.state.submitting;
    this.panel?.querySelectorAll<HTMLButtonElement>('.df-tool').forEach((b) => { b.disabled = busy; });
  }

  private addAttachment(blob: Blob, name: string, kind: AttachmentKind): void {
    const needsPreview = kind === 'screenshot' || (kind === 'file' && /^image\//.test(blob.type));
    const id = nextId('att');
    this.attachments.push({ id, blob, name, kind, previewUrl: null, status: 'queued' });
    this.renderEvidence();
    if (needsPreview) void this.hydratePreviewUrl(id, blob);
  }

  private async hydratePreviewUrl(attachmentId: string, blob: Blob): Promise<void> {
    try {
      const dataUrl = await blobToDataUrl(blob);
      const att = this.attachments.find((a) => a.id === attachmentId);
      if (!att) return;
      att.previewUrl = dataUrl;
      this.renderEvidence();
    } catch {
      // Preview failed — the file still uploads, shown as a file tile.
    }
  }

  private addFile(file: File): void {
    if (this.attachments.length >= MAX_FILES) {
      this.flashError(`You can attach up to ${MAX_FILES} files. Remove one first.`);
      return;
    }
    // Audio picked from disk is a file, not a voice note: it is judged by the
    // workspace's attachment rules like any other file.
    this.addAttachment(file, file.name, 'file');
  }

  private removeAttachment(id: string): void {
    this.attachments = this.attachments.filter((a) => a.id !== id);
    this.renderEvidence();
    this.panel?.querySelector<HTMLElement>('[data-action="file"]')?.focus();
  }

  private openAttachment(id: string): void {
    const att = this.attachments.find((a) => a.id === id);
    if (!att) return;
    if (att.kind === 'screenshot') void this.openAnnotator(att.id);
    else if (att.kind === 'recording') this.openPlayer(att.id);
  }

  private async openAnnotator(attachmentId: string): Promise<void> {
    const att = this.attachments.find((a) => a.id === attachmentId);
    if (!att || att.kind !== 'screenshot' || att.status !== 'queued') return;
    const annotated = await annotate(att.blob, this.root);
    if (!annotated) return;
    att.blob = annotated;
    att.previewUrl = null;
    this.renderEvidence();
    void this.hydratePreviewUrl(att.id, annotated);
  }

  private openPlayer(attachmentId: string): void {
    const att = this.attachments.find((a) => a.id === attachmentId);
    if (!att || att.kind !== 'recording') return;
    // playback.html on the Klientele backend builds the blob URL on its own
    // origin, so a strict host media-src cannot block it.
    const playerUrl = `${this.config.apiUrl}/widget/v1/playback.html`;
    const playerTab = window.open(playerUrl, '_blank');
    if (!playerTab) return; // popup blocker
    const { blob, name } = att;
    const targetOrigin = new URL(playerUrl).origin;
    setTimeout(() => {
      if (playerTab.closed) return;
      void blob.arrayBuffer().then((buffer) => {
        if (!playerTab.closed) {
          playerTab.postMessage({ type: 'df-player-data', buffer, mimeType: blob.type, name }, targetOrigin, [buffer]);
        }
      });
    }, 800);
  }

  // ------- Voice notes -------

  private voiceCapMs(): number {
    const server = this.config.server.voiceNotes.maxMs;
    return server > 0 ? Math.min(VOICE_CAP_MS, server) : VOICE_CAP_MS;
  }

  /** Start recording a new note, or re-record `replaceId`. */
  private async onVoiceClick(replaceId: string | null): Promise<void> {
    if (this.voice || this.state.submitting) return;
    const rules = this.config.server.voiceNotes;
    if (!rules.enabled) {
      this.flashError(`${this.firmName} isn't accepting attachments, so voice notes are off. Type it instead.`);
      return;
    }
    if (!replaceId && this.voiceNotes.length >= MAX_VOICE_NOTES) {
      this.flashError(`You can add up to ${MAX_VOICE_NOTES} voice notes. Remove one first.`);
      return;
    }
    this.stopPlayback();
    this.hideError();
    const capMs = this.voiceCapMs();
    const started = await startVoice(rules.mimeTypes, capMs);
    if (!this.state.open) { if (!('ok' in started)) started.cancel(); return; }
    if ('ok' in started) {
      this.flashError(started.message);
      return;
    }

    const tick = setInterval(() => this.paintLiveRecording(), 100);
    this.voice = { session: started, replaces: replaceId, tick };
    this.renderEvidence();
    this.panel?.querySelector<HTMLButtonElement>('[data-action="voice-stop"]')?.focus();

    const result = await started.result;
    clearInterval(tick);
    const replaces = this.voice?.replaces ?? null;
    this.voice = null;
    if (!this.state.open || !this.panel) return;

    if (result && result.durationMs >= 400) {
      if (rules.maxBytes > 0 && result.blob.size > rules.maxBytes) {
        this.flashError(`That voice note is too large to send (limit ${Math.round(rules.maxBytes / 1048576)} MB). Record a shorter one.`);
      } else {
        const note: VoiceNote = {
          id: nextId('voice'),
          blob: result.blob,
          durationMs: result.durationMs,
          bars: waveform(result.levels, BAR_COUNT),
          status: 'queued',
        };
        const at = replaces ? this.voiceNotes.findIndex((n) => n.id === replaces) : -1;
        if (at >= 0) this.voiceNotes.splice(at, 1, note);
        else this.voiceNotes.push(note);
      }
    }
    this.renderEvidence();
    const focusTarget = this.panel.querySelector<HTMLElement>('.df-voice:last-of-type .df-play') ?? this.panel.querySelector<HTMLElement>('[data-action="voice"]');
    focusTarget?.focus();
  }

  private paintLiveRecording(): void {
    if (!this.voice || !this.panel) return;
    const bars = this.panel.querySelector<HTMLElement>('[data-role="live-bars"]');
    const time = this.panel.querySelector<HTMLElement>('[data-role="live-time"]');
    if (bars) {
      // Scroll the level history right to left.
      const first = bars.firstElementChild;
      if (first) {
        bars.appendChild(first);
        (first as HTMLElement).style.height = `${Math.max(12, Math.round(this.voice.session.level() * 100))}%`;
      }
    }
    if (time) {
      time.textContent = `${formatDuration(Math.floor(this.voice.session.elapsedMs() / 1000))} / ${formatDuration(this.voiceCapMs() / 1000)}`;
    }
  }

  private cancelVoice(): void {
    if (!this.voice) return;
    clearInterval(this.voice.tick);
    this.voice.session.cancel();
    this.voice = null;
  }

  private removeVoiceNote(id: string): void {
    if (this.player?.noteId === id) this.stopPlayback();
    this.voiceNotes = this.voiceNotes.filter((n) => n.id !== id);
    this.renderEvidence();
    this.panel?.querySelector<HTMLElement>('[data-action="voice"]')?.focus();
  }

  private togglePlayback(id: string): void {
    if (this.player?.noteId === id) {
      this.stopPlayback();
      this.renderEvidence();
      this.panel?.querySelector<HTMLElement>(`[data-note="${id}"] .df-play`)?.focus();
      return;
    }
    this.stopPlayback();
    const note = this.voiceNotes.find((n) => n.id === id);
    if (!note) return;
    const url = URL.createObjectURL(note.blob);
    const audio = new Audio(url);
    const paint = () => {
      if (!this.player || this.player.noteId !== id) return;
      const row = this.panel?.querySelector<HTMLElement>(`[data-note="${id}"]`);
      const frac = note.durationMs > 0 ? Math.min(1, (audio.currentTime * 1000) / note.durationMs) : 0;
      row?.querySelectorAll('.df-bars b').forEach((b, i) => b.classList.toggle('df-played', i / BAR_COUNT < frac));
      this.player.raf = requestAnimationFrame(paint);
    };
    audio.addEventListener('ended', () => {
      this.stopPlayback();
      this.renderEvidence();
      this.panel?.querySelector<HTMLElement>(`[data-note="${id}"] .df-play`)?.focus();
    });
    this.player = { audio, noteId: id, url, raf: 0 };
    audio.play().then(() => {
      this.renderEvidence();
      this.panel?.querySelector<HTMLElement>(`[data-note="${id}"] .df-play`)?.focus();
      if (this.player) this.player.raf = requestAnimationFrame(paint);
    }).catch(() => {
      // A strict host media-src can block blob: audio. The note still sends.
      this.stopPlayback();
      this.flashError("This page doesn't allow playback here. Your voice note will still be sent.");
    });
  }

  private stopPlayback(): void {
    if (!this.player) return;
    cancelAnimationFrame(this.player.raf);
    this.player.audio.pause();
    URL.revokeObjectURL(this.player.url);
    this.player = null;
  }

  // ------- Screenshot + screen recording -------

  private async onScreenshotClick(): Promise<void> {
    if (this.attachments.length >= MAX_FILES) {
      this.flashError(`You can attach up to ${MAX_FILES} files. Remove one first.`);
      return;
    }
    const btn = this.panel?.querySelector<HTMLButtonElement>('[data-action="screenshot"]');
    const original = btn?.innerHTML ?? null;
    if (btn) { btn.disabled = true; btn.innerHTML = SPINNER_SVG; btn.setAttribute('aria-busy', 'true'); }
    try {
      const result = await captureScreenshot();
      if (result.ok) {
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        this.addAttachment(result.blob, `screenshot-${stamp}.png`, 'screenshot');
      } else if (result.reason !== 'denied') {
        this.flashError(result.message);
      }
    } finally {
      if (btn && original !== null) { btn.disabled = false; btn.innerHTML = original; btn.removeAttribute('aria-busy'); btn.focus(); }
    }
  }

  private async onRecordClick(): Promise<void> {
    if (this.recording) return;
    if (this.attachments.length >= MAX_FILES) {
      this.flashError(`You can attach up to ${MAX_FILES} files. Remove one first.`);
      return;
    }

    // Collapse panel + launcher so the page is clear during the share picker.
    this.modalEl?.classList.add('df-hidden');
    this.launcher.classList.add('df-hidden');

    const handle = await startScreenRecording();
    this.recording = handle;
    this.recordingOverlay = installRecordingOverlay(this.config.pointerColor);

    const maxSecs = Math.floor(RECORDING_LIMITS.maxMs / 1000);
    const pill = document.createElement('div');
    pill.className = 'df-rec-pill';
    pill.setAttribute('role', 'status');
    pill.innerHTML = `<span class="df-recording-dot"></span><span class="df-rec-pill-timer">0:00 / ${formatDuration(maxSecs)}</span><button type="button" class="df-rec-pill-stop">${STOP_SVG}<span>Stop</span></button>`;
    this.root.appendChild(pill);
    const pillTimer = pill.querySelector<HTMLSpanElement>('.df-rec-pill-timer')!;
    pill.querySelector('.df-rec-pill-stop')?.addEventListener('click', () => this.stopRecording());

    this.recordingInterval = setInterval(() => {
      if (!this.recording) return;
      const elapsed = Math.floor(this.recording.getElapsedMs() / 1000);
      pillTimer.textContent = `${formatDuration(elapsed)} / ${formatDuration(maxSecs)}`;
    }, 250);

    const result = await handle.result;
    this.recording = null;
    if (this.recordingInterval) { clearInterval(this.recordingInterval); this.recordingInterval = null; }
    pill.remove();
    this.recordingOverlay?.cleanup();
    this.recordingOverlay = null;
    if (this.state.open && this.modalEl) {
      this.modalEl.classList.remove('df-hidden');
      this.evaluateVisibility();
    }

    if (result.ok) {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const ext = result.mimeType.includes('mp4') ? 'mp4' : 'webm';
      this.addAttachment(result.blob, `recording-${stamp}.${ext}`, 'recording');
    } else if (result.reason !== 'denied') {
      this.flashError(result.message);
    }
    this.panel?.querySelector<HTMLElement>('[data-action="record"]')?.focus();
  }

  private stopRecording(): void {
    if (this.recording) { try { this.recording.stop(); } catch { /* already stopped */ } }
  }

  // ------- Send -------

  private async onSubmit(form: HTMLFormElement): Promise<void> {
    if (this.state.submitting || this.voice) return;
    const subjectEl = form.querySelector<HTMLInputElement>('#df-subject')!;
    const detailsEl = form.querySelector<HTMLTextAreaElement>('#df-details')!;
    const emailEl = form.querySelector<HTMLInputElement>('#df-email')!;
    const sendBtn = form.querySelector<HTMLButtonElement>('[data-role="send"]')!;
    const sendLabel = form.querySelector<HTMLSpanElement>('[data-role="send-label"]')!;

    const subject = subjectEl.value.trim();
    const details = detailsEl.value.trim();
    const email = emailEl.value.trim();

    if (!subject && !details && this.voiceNotes.length === 0) {
      this.flashError('Add a subject, a few details or a voice note.');
      subjectEl.focus();
      return;
    }
    if (this.config.requireEmail && !email) {
      this.flashError('Add your email so the reply can reach you.');
      emailEl.setAttribute('aria-invalid', 'true');
      emailEl.focus();
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.flashError('That email address looks incomplete.');
      emailEl.setAttribute('aria-invalid', 'true');
      emailEl.focus();
      return;
    }

    this.stopPlayback();
    this.hideError();
    this.state.submitting = true;
    sendBtn.disabled = true;
    sendLabel.textContent = 'Sending…';
    this.updateToolState();

    // A request with no typed subject takes its first line, or names the voice note.
    const title = subject || firstLine(details) || 'Voice note';
    const urgency = this.config.server.urgency.find((u) => u.key === this.draft.urgency)?.urgency;
    let succeeded = false;

    try {
      const created = await submitRequest(this.config, {
        title,
        description: details || null,
        urgency: urgency ?? 'medium',
        reporterEmail: email || null,
        reporterName: null,
        context: captureContext(),
      });
      if (!created.ok || !created.requestNumber) {
        this.flashError(created.errorMessage ?? "Couldn't send. Check your connection and try again.");
        return;
      }
      const ref = created.requestNumber;

      const total = this.attachments.length + this.voiceNotes.length;
      if (total > 0) {
        let done = 0;
        sendLabel.textContent = `Uploading 0 of ${total}`;
        const step = () => { done++; sendLabel.textContent = `Uploading ${done} of ${total}`; };
        const files = this.attachments.map(async (att) => {
          att.status = 'uploading';
          this.renderEvidence();
          const r = await uploadAttachment(this.config, ref, att.blob, att.name);
          att.status = r.ok ? 'uploaded' : 'failed';
          att.errorMessage = r.ok ? undefined : r.errorMessage;
          step();
          this.renderEvidence();
        });
        const notes = this.voiceNotes.map(async (n) => {
          n.status = 'uploading';
          const r = await uploadVoiceNote(this.config, ref, n.blob, n.durationMs);
          n.status = r.ok ? 'uploaded' : 'failed';
          n.errorMessage = r.ok ? undefined : r.errorMessage;
          step();
        });
        await Promise.all([...files, ...notes]);
      }

      this.renderDone(ref, email || null);
      succeeded = true;
    } catch (err) {
      this.flashError(err instanceof Error ? err.message : "Couldn't send. Try again.");
    } finally {
      this.state.submitting = false;
      if (!succeeded) {
        sendBtn.disabled = false;
        sendLabel.textContent = 'Send';
        this.updateToolState();
      }
    }
  }

  private hideError(): void {
    this.panel?.querySelector<HTMLElement>('[data-role="error"]')?.classList.add('df-hidden');
  }

  private flashError(message: string): void {
    const banner = this.panel?.querySelector<HTMLDivElement>('[data-role="error"]');
    if (!banner) return;
    banner.textContent = message;
    banner.classList.remove('df-hidden');
  }

  // ------- After sending -------

  private renderDone(reference: string, email: string | null): void {
    const panel = this.panel;
    if (!panel) return;
    const firm = this.firmName;
    const noun = this.config.server.nouns.request;
    const ok = <T extends { status: UploadStatus }>(xs: T[]) => xs.filter((x) => x.status === 'uploaded');
    const failed = [...this.attachments, ...this.voiceNotes].filter((x) => x.status === 'failed').length;

    const notes = ok(this.voiceNotes).length;
    const shots = ok(this.attachments).filter((a) => a.kind === 'screenshot').length;
    const recs = ok(this.attachments).filter((a) => a.kind === 'recording').length;
    const files = ok(this.attachments).filter((a) => a.kind === 'file').length;
    const parts = [
      notes ? (notes === 1 ? 'your voice note' : `${notes} voice notes`) : '',
      shots ? plural(shots, 'screenshot') : '',
      recs ? plural(recs, 'screen recording') : '',
      files ? plural(files, 'file') : '',
      'the page details',
    ].filter(Boolean);
    const received = `With ${joinList(parts)}.`;
    const failedLine = failed > 0
      ? `<p class="df-warn">${failed === 1 ? "1 attachment couldn't be uploaded" : `${failed} attachments couldn't be uploaded`}. Mention it when ${escapeHtml(firm)} replies.</p>`
      : '';
    const replies = email
      ? `${firm} will reply to ${email}.`
      : `Quote this reference if you contact ${firm}.`;

    panel.setAttribute('aria-label', `${noun} sent to ${firm}`);
    panel.innerHTML = `
      ${this.headerHtml('Sent')}
      <div class="df-modal-scroll">
        <div class="df-done">
          <div class="df-tick" aria-hidden="true">${TICK_SVG}</div>
          <h3 class="df-done-title" tabindex="-1" data-role="done-title">${escapeHtml(firm)} has it</h3>
          <p>${escapeHtml(received)}</p>
          ${failedLine}
          ${this.config.successMessage ? `<p>${escapeHtml(this.config.successMessage)}</p>` : ''}
          <div class="df-ref">
            <span><span class="df-ref-label">${escapeHtml(noun)} reference</span><span class="df-ref-num" data-role="ref">${escapeHtml(reference)}</span></span>
            <button type="button" class="df-copy" data-role="copy" aria-label="Copy reference ${escapeAttr(reference)}">Copy</button>
          </div>
          <p class="df-note">${escapeHtml(replies)}</p>
          <button type="button" class="df-send df-ghost df-full" data-role="again">Send something else</button>
          <span class="df-sr" role="status">${escapeHtml(`${firm} has it. Reference ${reference}.`)}</span>
        </div>
      </div>
      ${this.footerHtml()}
    `;
    this.wireLogoFallback();

    // The draft is spent; "Send something else" starts clean.
    this.attachments = [];
    this.voiceNotes = [];
    this.draft = this.emptyDraft();

    panel.onclick = (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-action="close"]')) this.close();
      else if (t.closest('[data-role="again"]')) this.renderForm();
      else if (t.closest('[data-role="copy"]')) void this.copyReference(reference);
    };
    queueMicrotask(() => panel.querySelector<HTMLElement>('[data-role="done-title"]')?.focus());
  }

  private async copyReference(reference: string): Promise<void> {
    const btn = this.panel?.querySelector<HTMLButtonElement>('[data-role="copy"]');
    let copied = false;
    try {
      await navigator.clipboard.writeText(reference);
      copied = true;
    } catch {
      // Clipboard API blocked (permissions policy / insecure page): select the text.
      const num = this.panel?.querySelector<HTMLElement>('[data-role="ref"]');
      const sel = window.getSelection();
      if (num && sel) {
        const range = document.createRange();
        range.selectNodeContents(num);
        sel.removeAllRanges();
        sel.addRange(range);
        copied = document.execCommand?.('copy') ?? false;
      }
    }
    if (btn) {
      btn.textContent = copied ? 'Copied' : 'Select and copy';
      setTimeout(() => { if (btn.isConnected) btn.textContent = 'Copy'; }, 2000);
    }
  }
}

function initials(name: string): string {
  const words = name.replace(/[^\p{L}\p{N}\s&]/gu, ' ').split(/\s+/).filter((w) => w && w !== '&');
  const letters = words.length >= 2 ? words[0]![0]! + words[1]![0]! : (words[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

function firstLine(text: string): string {
  const line = text.split('\n').find((l) => l.trim())?.trim() ?? '';
  return line.length > 80 ? `${line.slice(0, 77)}…` : line;
}

function plural(n: number, word: string): string {
  return n === 1 ? `1 ${word}` : `${n} ${word}s`;
}

function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
const escapeAttr = escapeHtml;

/**
 * Reads a Blob as a `data:` URL for thumbnails — blob: URLs fail strict
 * host CSPs under `img-src 'self' data: https:`.
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolveResult, rejectResult) => {
    const reader = new FileReader();
    reader.onload = () => resolveResult(reader.result as string);
    reader.onerror = () => rejectResult(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
